import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useNavigate } from "react-router-dom";
import { api } from "../utils/api";
import { useApp } from "../context/AppContext";
import { t } from "../utils/i18n";
import { docTypeI18nKey } from "../utils/verificationDocs";
import UiIcon from "./UiIcon";
import { UiIconName } from "../utils/uiIcons";
import { K } from "./UiKit";

// Suivi de vérification façon LinkedIn — ouvert en touchant le badge à côté d'un nom (profil
// personnel ou profil visité). Données : GET /users/:id/verification-info (dates + nature de la
// pièce, jamais d'image ni de nom réel). Pour soi-même, s'ajoutent les étapes de la demande.
type Row = { icon: UiIconName; label: string; value: string; sub?: string };

export default function VerificationTracker({ userId, onClose }: { userId: string; onClose: () => void }) {
  const { lang, user } = useApp();
  const navigate = useNavigate();
  const [info, setInfo] = useState<any>(undefined);
  const isSelf = userId === user?.id;

  useEffect(() => {
    api.get(`/users/${isSelf ? "me" : userId}/verification-info`).then(d => setInfo(d?.info || null)).catch(() => setInfo(null));
  }, [userId, isSelf]);

  const fmt = (d?: string | null) => d ? new Date(d).toLocaleDateString(lang, { day: "numeric", month: "long", year: "numeric" }) : "";

  const rows: Row[] = [];
  if (info) {
    rows.push({ icon: "user", label: t(lang, "vfy_track_created"), value: fmt(info.account_created_at) });
    if (info.is_verified) rows.push({ icon: "shieldCheck", label: t(lang, "vfy_track_verified"), value: info.verified_at ? fmt(info.verified_at) : "✓" });
    if (info.name_updated_at) rows.push({ icon: "pencil", label: t(lang, "vfy_track_name_updated"), value: fmt(info.name_updated_at) });
    rows.push({
      icon: "file",
      label: t(lang, "vfy_track_doc"),
      value: info.document ? t(lang, docTypeI18nKey(info.document.type)) : t(lang, "vfy_track_doc_none"),
      sub: info.document?.added_at ? t(lang, "vfy_track_added_on").replace("{date}", fmt(info.document.added_at)) : undefined,
    });
  }

  const req = info?.request;
  const steps = isSelf && req ? [
    { label: t(lang, "vfy_sent_title"), sub: fmt(req.submitted_at), done: true },
    { label: t(lang, "vfy_step_review"), sub: req.status === "pending" ? t(lang, "verify_pending_review") : "", done: req.status !== "pending", current: req.status === "pending" },
    req.status === "rejected"
      ? { label: t(lang, "vfy_step_rejected"), sub: fmt(req.reviewed_at), done: true }
      : { label: t(lang, "vfy_track_verified_title"), sub: req.status === "approved" ? fmt(req.reviewed_at) : "", done: req.status === "approved" },
  ] : [];

  return ReactDOM.createPortal(
    <div onClick={onClose} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.55)", zIndex: 3000, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()} style={{ backgroundColor: K.white, borderRadius: "22px 22px 0 0", width: "100%", maxWidth: 520, maxHeight: "86vh", overflowY: "auto", padding: "10px 20px 28px", boxSizing: "border-box" }}>
        <div style={{ width: 40, height: 4, borderRadius: 2, backgroundColor: "#D9D9D9", margin: "0 auto 18px" }} />
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
          <div style={{ width: 52, height: 52, borderRadius: 26, backgroundColor: info?.is_verified ? K.ink : K.surface, color: info?.is_verified ? K.white : K.ink, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <UiIcon name="shieldCheck" size={26} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 19, fontWeight: 700, letterSpacing: "-0.01em" }}>{info && !info.is_verified ? t(lang, "vfy_track_not_verified") : t(lang, "vfy_track_verified_title")}</div>
            <div style={{ fontSize: 12.5, color: K.muted, marginTop: 2 }}>{t(lang, "vfy_track_title")}</div>
          </div>
        </div>

        {info === undefined ? (
          <div style={{ textAlign: "center", padding: 30, color: K.faint }}>{t(lang, "msg_loading")}</div>
        ) : !info ? (
          <div style={{ textAlign: "center", padding: 30, color: K.faint }}>{t(lang, "error_connection")}</div>
        ) : (
          <>
            {steps.length > 0 && info.request?.status !== "approved" && (
              <div style={{ marginBottom: 18 }}>
                {steps.map((s, i) => (
                  <div key={i} style={{ display: "flex", gap: 12 }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                      <div style={{ width: 24, height: 24, borderRadius: 12, backgroundColor: s.done ? K.ink : K.white, border: `2px solid ${s.done || (s as any).current ? K.ink : "#D0D0D0"}`, color: K.white, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        {s.done ? <UiIcon name="check" size={13} strokeWidth={2.8} /> : (s as any).current ? <div style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: K.ink }} /> : null}
                      </div>
                      {i < steps.length - 1 && <div style={{ width: 2, flex: 1, minHeight: 22, backgroundColor: s.done ? K.ink : "#E3E3E3" }} />}
                    </div>
                    <div style={{ paddingBottom: 14, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: s.done || (s as any).current ? K.ink : K.faint }}>{s.label}</div>
                      {!!s.sub && <div style={{ fontSize: 12.5, color: K.muted, marginTop: 2, lineHeight: 1.4 }}>{s.sub}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div style={{ border: `1px solid ${K.line}`, borderRadius: 16, padding: "4px 14px" }}>
              {rows.map((r, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: i < rows.length - 1 ? `1px solid ${K.line}` : "none" }}>
                  <div style={{ width: 34, height: 34, borderRadius: 17, backgroundColor: K.surface, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <UiIcon name={r.icon} size={17} />
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 11.5, color: K.faint }}>{r.label}</div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{r.value}</div>
                    {r.sub && <div style={{ fontSize: 12, color: K.muted, marginTop: 1 }}>{r.sub}</div>}
                  </div>
                </div>
              ))}
            </div>

            {isSelf && (
              <button onClick={() => { onClose(); navigate("/verify-identity"); }} style={{ width: "100%", height: 50, borderRadius: 12, marginTop: 14, backgroundColor: K.surface, color: K.ink, fontSize: 14.5, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                <UiIcon name="plus" size={17} />{t(lang, "vfy_settings_add_doc")}
              </button>
            )}
          </>
        )}
        <button onClick={onClose} style={{ width: "100%", height: 50, borderRadius: 12, marginTop: 10, backgroundColor: K.ink, color: K.white, fontSize: 15, fontWeight: 600 }}>{t(lang, "vfy_track_close")}</button>
      </div>
    </div>,
    document.body
  );
}
